import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { auth as apiAuth, getUserInfo, clearApiToken } from '../api/cms';

export type UserRole = 'management' | 'vanzari' | 'marketing';

export interface CmsUser {
  id: string;
  username: string;
  displayName: string;
  email: string;
  role: UserRole;
  avatar?: string;
  createdAt: string;
  lastLogin?: string;
  active: boolean;
}

export interface ActivityLog {
  id: string;
  userId: string;
  username: string;
  displayName: string;
  role: UserRole;
  action: string;
  section: string;
  detail: string;
  timestamp: string;
}

const DEFAULT_USERS: CmsUser[] = [
  {
    id: 'usr-1', username: 'admin', displayName: 'Administrator',
    email: 'office@interpersonal.ro', role: 'management',
    createdAt: '2024-01-01', active: true,
  },
  {
    id: 'usr-2', username: 'marketing', displayName: 'Marketing & Content',
    email: 'marketing@interpersonal.ro', role: 'marketing',
    createdAt: '2025-01-01', active: true,
  },
  {
    id: 'usr-3', username: 'vanzari', displayName: 'Echipa Vânzări',
    email: 'vanzari@interpersonal.ro', role: 'vanzari',
    createdAt: '2025-01-01', active: true,
  },
];

const USER_PASSWORDS: Record<string, string> = {
  'usr-1': '1234qwer',
  'usr-2': 'marketing2026',
  'usr-3': 'vanzari2026',
};

export const ROLE_LABELS: Record<UserRole, string> = {
  management: 'Management',
  vanzari: 'Vânzări',
  marketing: 'Marketing & Content',
};

export const ROLE_COLORS: Record<UserRole, string> = {
  management: 'text-purple-400 bg-purple-400/10 border-purple-400/20',
  vanzari: 'text-green-400 bg-green-400/10 border-green-400/20',
  marketing: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
};

// Permisiuni per rol: ce secțiuni pot accesa
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  management: ['dashboard', 'blog', 'media', 'pages', 'products', 'orders', 'clients', 'prospects', 'financial', 'settings', 'seo', 'chatbot', 'curierat', 'users', 'help'],
  vanzari: ['products', 'orders', 'clients', 'prospects', 'media', 'help'],
  marketing: ['blog', 'pages', 'products', 'seo', 'media', 'help'],
};

interface AuthContextValue {
  currentUser: CmsUser | null;
  allUsers: CmsUser[];
  activityLogs: ActivityLog[];
  login: (username: string, password: string) => boolean;
  logout: () => void;
  isLoggedIn: () => boolean;
  hasAccess: (section: string) => boolean;
  changePassword: (userId: string, oldPass: string, newPass: string) => { ok: boolean; error?: string };
  saveUser: (user: CmsUser, password?: string) => void;
  deleteUser: (userId: string) => void;
  logActivity: (action: string, section: string, detail: string) => void;
  loadUsers: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<CmsUser | null>(null);
  const [allUsers, setAllUsers] = useState<CmsUser[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [passwords, setPasswords] = useState<Record<string, string>>({});

  const loadUsers = useCallback(() => {
    const stored = localStorage.getItem('cms_users');
    const storedPw = localStorage.getItem('cms_passwords');
    if (stored) {
      setAllUsers(JSON.parse(stored));
    } else {
      setAllUsers(DEFAULT_USERS);
      localStorage.setItem('cms_users', JSON.stringify(DEFAULT_USERS));
    }
    if (storedPw) {
      setPasswords(JSON.parse(storedPw));
    } else {
      setPasswords(USER_PASSWORDS);
      localStorage.setItem('cms_passwords', JSON.stringify(USER_PASSWORDS));
    }
    const storedLogs = localStorage.getItem('cms_activity_logs');
    if (storedLogs) setActivityLogs(JSON.parse(storedLogs));
  }, []);

  useEffect(() => {
    loadUsers();
    const storedUser = localStorage.getItem('cms_current_user');
    if (storedUser) setCurrentUser(JSON.parse(storedUser));
  }, [loadUsers]);

  const login = useCallback((username: string, password: string): boolean => {
    // Login asincron — apeleaza PHP API si obtine token real
    // Returneaza true imediat (optimistic) si face request in background
    apiAuth.login(username, password).then(result => {
      if (!result.ok) {
        // Credentiale gresite conform PHP — delogheaza
        clearApiToken();
        setCurrentUser(null);
        return;
      }
      // Actualizeaza rolul din raspunsul PHP
      const roleFromApi = (result.role as UserRole) ?? 'management';
      const users: CmsUser[] = JSON.parse(localStorage.getItem('cms_users') || JSON.stringify(DEFAULT_USERS));
      let user = users.find(u => u.username === username && u.active);
      if (!user) {
        // User nou (nu exista in lista locala) — creeaza entry
        user = DEFAULT_USERS.find(u => u.username === username) ??
          { id: `usr-${Date.now()}`, username, displayName: username, email: '', role: roleFromApi, createdAt: new Date().toISOString().split('T')[0], active: true };
      }
      const updated = { ...user, role: roleFromApi, lastLogin: new Date().toISOString() };
      setCurrentUser(updated);
      localStorage.setItem('cms_current_user', JSON.stringify(updated));
    }).catch(() => { /* PHP offline — fallback deja setat in apiAuth.login */ });

    // Verificare sincrona (fallback local) pentru UX imediat
    const users: CmsUser[] = JSON.parse(localStorage.getItem('cms_users') || JSON.stringify(DEFAULT_USERS));
    const pws: Record<string, string> = JSON.parse(localStorage.getItem('cms_passwords') || JSON.stringify(USER_PASSWORDS));
    const user = users.find(u => u.username === username && u.active);
    if (!user) return false;
    const correctPw = pws[user.id] ?? USER_PASSWORDS[user.id];
    if (correctPw !== password) return false;
    const updated = { ...user, lastLogin: new Date().toISOString() };
    setCurrentUser(updated);
    localStorage.setItem('cms_auth', '1');
    localStorage.setItem('cms_current_user', JSON.stringify(updated));
    const log: ActivityLog = {
      id: `log-${Date.now()}`, userId: user.id, username: user.username,
      displayName: user.displayName, role: user.role,
      action: 'LOGIN', section: 'Sistem', detail: 'Autentificare reușită',
      timestamp: new Date().toISOString(),
    };
    const logs: ActivityLog[] = JSON.parse(localStorage.getItem('cms_activity_logs') || '[]');
    logs.unshift(log);
    const trimmed = logs.slice(0, 500);
    localStorage.setItem('cms_activity_logs', JSON.stringify(trimmed));
    setActivityLogs(trimmed);
    return true;
  }, []);

  const logout = useCallback(() => {
    if (currentUser) {
      const log: ActivityLog = {
        id: `log-${Date.now()}`, userId: currentUser.id, username: currentUser.username,
        displayName: currentUser.displayName, role: currentUser.role,
        action: 'LOGOUT', section: 'Sistem', detail: 'Deconectare',
        timestamp: new Date().toISOString(),
      };
      const logs: ActivityLog[] = JSON.parse(localStorage.getItem('cms_activity_logs') || '[]');
      logs.unshift(log);
      localStorage.setItem('cms_activity_logs', JSON.stringify(logs.slice(0, 500)));
    }
    // Revocare token pe server (async, nu blocheaza UI)
    apiAuth.logout().catch(() => {});
    setCurrentUser(null);
    localStorage.removeItem('cms_auth');
    localStorage.removeItem('cms_current_user');
  }, [currentUser]);

  const isLoggedIn = useCallback(() => {
    // Pe server live: verifica si token-ul API
    const hasLocalAuth = !!currentUser || localStorage.getItem('cms_auth') === '1';
    // Daca avem user din raspunsul PHP (getUserInfo), actualizeaza rolul
    if (hasLocalAuth && !currentUser) {
      const info = getUserInfo();
      if (info) {
        const users: CmsUser[] = JSON.parse(localStorage.getItem('cms_users') || JSON.stringify(DEFAULT_USERS));
        const user = users.find(u => u.username === info.user);
        if (user) setCurrentUser({ ...user, role: info.role as UserRole });
      }
    }
    return hasLocalAuth;
  }, [currentUser]);

  const hasAccess = useCallback((section: string): boolean => {
    const user = currentUser ?? (() => {
      const s = localStorage.getItem('cms_current_user');
      return s ? JSON.parse(s) as CmsUser : null;
    })();
    if (!user) return false;
    return ROLE_PERMISSIONS[user.role]?.includes(section) ?? false;
  }, [currentUser]);

  const changePassword = useCallback((userId: string, oldPass: string, newPass: string) => {
    const pws: Record<string, string> = JSON.parse(localStorage.getItem('cms_passwords') || JSON.stringify(USER_PASSWORDS));
    const users: CmsUser[] = JSON.parse(localStorage.getItem('cms_users') || JSON.stringify(DEFAULT_USERS));
    const user = users.find(u => u.id === userId);
    if (!user) return { ok: false, error: 'Utilizatorul nu există.' };
    const current = currentUser;
    // Only management can change other users' passwords without old pw
    if (current?.role !== 'management' || current.id === userId) {
      const correctPw = pws[userId] ?? USER_PASSWORDS[userId];
      if (correctPw !== oldPass) return { ok: false, error: 'Parola curentă este incorectă.' };
    }
    if (newPass.length < 6) return { ok: false, error: 'Parola nouă trebuie să aibă minim 6 caractere.' };
    pws[userId] = newPass;
    localStorage.setItem('cms_passwords', JSON.stringify(pws));
    setPasswords({ ...pws });
    logActivity('CHANGE_PASSWORD', 'Utilizatori', `Parolă schimbată pentru ${user.displayName}`);
    return { ok: true };
  }, [currentUser]);

  const saveUser = useCallback((user: CmsUser, password?: string) => {
    const users: CmsUser[] = JSON.parse(localStorage.getItem('cms_users') || JSON.stringify(DEFAULT_USERS));
    const pws: Record<string, string> = JSON.parse(localStorage.getItem('cms_passwords') || JSON.stringify(USER_PASSWORDS));
    const idx = users.findIndex(u => u.id === user.id);
    if (idx >= 0) {
      users[idx] = user;
    } else {
      users.push(user);
    }
    localStorage.setItem('cms_users', JSON.stringify(users));
    setAllUsers([...users]);
    if (password) {
      pws[user.id] = password;
      localStorage.setItem('cms_passwords', JSON.stringify(pws));
      setPasswords({ ...pws });
    }
    logActivity('SAVE_USER', 'Utilizatori', `User ${user.displayName} (${user.role}) salvat`);
  }, []);

  const deleteUser = useCallback((userId: string) => {
    const users: CmsUser[] = JSON.parse(localStorage.getItem('cms_users') || JSON.stringify(DEFAULT_USERS));
    const u = users.find(x => x.id === userId);
    const filtered = users.filter(x => x.id !== userId);
    localStorage.setItem('cms_users', JSON.stringify(filtered));
    setAllUsers(filtered);
    logActivity('DELETE_USER', 'Utilizatori', `User ${u?.displayName} șters`);
  }, []);

  const logActivity = useCallback((action: string, section: string, detail: string) => {
    const user = currentUser ?? (() => {
      const s = localStorage.getItem('cms_current_user');
      return s ? JSON.parse(s) as CmsUser : null;
    })();
    if (!user) return;
    const log: ActivityLog = {
      id: `log-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      userId: user.id, username: user.username,
      displayName: user.displayName, role: user.role,
      action, section, detail,
      timestamp: new Date().toISOString(),
    };
    const logs: ActivityLog[] = JSON.parse(localStorage.getItem('cms_activity_logs') || '[]');
    logs.unshift(log);
    const trimmed = logs.slice(0, 500);
    localStorage.setItem('cms_activity_logs', JSON.stringify(trimmed));
    setActivityLogs(trimmed);
  }, [currentUser]);

  return (
    <AuthContext.Provider value={{ currentUser, allUsers, activityLogs, login, logout, isLoggedIn, hasAccess, changePassword, saveUser, deleteUser, logActivity, loadUsers }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
